import React from 'react';
import { i18n } from 'i18n';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { UnitSystem } from 'providers/api';
import { distanceMetricToImperial } from 'utils/unitConversions';
import { useMarquee } from '../../contexts/MarqueeContext';
import { Model } from '../../hooks/useModelState';
import * as PCConstants from '../../MineModel/PCConstants';
import { sumSpoolLengths } from '../../MineModel/Spools';
import {
  getBulkEditSceneState,
  updateBulkEditSceneState,
  subscribeToBulkEditSceneState,
  BulkEditEntityType
} from './bulkEditSceneState';
import BulkEditPropertiesPanel from './BulkEditPropertiesPanel';

interface BulkEditPanelProps {
  model: Model;
}

const HIGHLIGHT_COLOUR = '#fd0';

const getDefaultNodeColour = (node: { isStope?: boolean }) => {
  if (node.isStope) return PCConstants.STOPE_COLOUR;
  return PCConstants.NODE_COLOUR;
};

const getDefaultNodeSize = (node: { isStope?: boolean }) => {
  if (node.isStope) return PCConstants.STOPE_RADIUS;
  return PCConstants.NODE_RADIUS;
};

const highlightSelectedNodes = (model: Model, selectedIds: string[]) => {
  const selectedSet = new Set(selectedIds);
  const updates = Object.entries(model.nodes).map(([nodeId, node]) => ({
    nodeId,
    nodeColour: selectedSet.has(nodeId) ? HIGHLIGHT_COLOUR : getDefaultNodeColour(node),
    nodeSize: selectedSet.has(nodeId) ? getDefaultNodeSize(node) * 1.5 : getDefaultNodeSize(node),
  }));
  model.batchUpdateNodeAppearance(updates);
};

const highlightSelectedSpools = (model: Model, selectedIds: string[]) => {
  const selectedSet = new Set(selectedIds);
  const spoolUpdates = Object.entries(model.spools).map(([spoolId, spool]) => ({
    spoolId,
    spoolColour: selectedSet.has(spoolId) ? HIGHLIGHT_COLOUR : model.resolveSpoolBaseColour(spool),
  }));
  model.batchUpdateSpoolAppearance(spoolUpdates);
  const nodeUpdates = Object.entries(model.nodes).map(([nodeId, node]) => ({
    nodeId,
    nodeColour: getDefaultNodeColour(node),
    nodeSize: getDefaultNodeSize(node),
  }));
  model.batchUpdateNodeAppearance(nodeUpdates);
};

const formatLength = (metres: number, isImperial: boolean): string => (
  isImperial
    ? `${Number(distanceMetricToImperial(metres).toFixed(1))} ft`
    : `${Number(metres.toFixed(1))} m`
);

const clearAllHighlights = (model: Model) => {
  const nodeUpdates = Object.entries(model.nodes).map(([nodeId, node]) => ({
    nodeId,
    nodeColour: getDefaultNodeColour(node),
    nodeSize: getDefaultNodeSize(node),
  }));
  model.batchUpdateNodeAppearance(nodeUpdates);
  const spoolUpdates = Object.entries(model.spools).map(([spoolId, spool]) => ({
    spoolId, spoolColour: model.resolveSpoolBaseColour(spool),
  }));
  model.batchUpdateSpoolAppearance(spoolUpdates);
};

const BulkEditPanel = ({ model }: BulkEditPanelProps) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const marquee = useMarquee();
  const sceneState = getBulkEditSceneState();
  const { entityType, isDeleting, selectedIds } = sceneState;
  const prevSelectedIdsRef = React.useRef<string[]>([]);
  const prevLoadGenerationRef = React.useRef(model.loadGeneration);
  const modelRef = React.useRef(model);
  modelRef.current = model;
  const selectedSpools = selectedIds.map((id) => model.spools[id]).filter(Boolean);
  const { pipeMetres, flowLoopMetres } = sumSpoolLengths(selectedSpools);
  const isImperial = model.project?.unitSystemPreference === UnitSystem.Imperial;

  const handleEntityTypeChange = (event: SelectChangeEvent<BulkEditEntityType>) => {
    if (isDeleting) return;

    const nextType = event.target.value as BulkEditEntityType;
    clearAllHighlights(model);
    marquee.setMarqueeMode(false);
    const state = getBulkEditSceneState();
    const allStopes = Object.values(model.stopes).flat();
    const stopeIdSet = new Set(allStopes.map((s) => s?.data?.stopeId).filter(Boolean) as string[]);
    const nextSelectedIds = (() => {
      if (nextType === 'All') return state.selectedIds;
      if (nextType === 'Node') return state.selectedIds.filter((id) => !!model.nodes[id]);
      if (nextType === 'Spool') return state.selectedIds.filter((id) => !!model.spools[id]);
      if (nextType === 'Stope') return state.selectedIds.filter((id) => stopeIdSet.has(id));
      return [];
    })();
    updateBulkEditSceneState({
      entityType: nextType,
      isSelecting: false,
      selectedIds: nextSelectedIds,
    });
  };

  const handleClearSelection = () => {
    if (isDeleting) return;

    clearAllHighlights(model);
    updateBulkEditSceneState({ selectedIds: [] });
  };

  // A save that refetches the model rebuilds every node and spool, so the highlight is repainted onto the new objects.
  React.useEffect(() => {
    const isSelectionChanged = prevSelectedIdsRef.current !== selectedIds;
    const isModelRebuilt = prevLoadGenerationRef.current !== model.loadGeneration;
    if (!isSelectionChanged && !isModelRebuilt) return;
    prevSelectedIdsRef.current = selectedIds;
    prevLoadGenerationRef.current = model.loadGeneration;
    const m = modelRef.current;

    if (entityType === 'Node') {
      highlightSelectedNodes(m, selectedIds);
    } else if (entityType === 'Stope') {
      const selectedSet = new Set(selectedIds);
      const allStopes = Object.values(m.stopes).flat();
      const stopeNodeIds = allStopes
        .filter((s) => selectedSet.has(s?.data?.stopeId))
        .map((s) => s?.originNode?.id)
        .filter(Boolean) as string[];
      highlightSelectedNodes(m, stopeNodeIds);
    } else if (entityType === 'Spool') {
      highlightSelectedSpools(m, selectedIds);
    } else if (entityType === 'All') {
      const selectedSet = new Set(selectedIds);
      const spoolUpdates = Object.entries(m.spools).map(([spoolId, spool]) => ({
        spoolId,
        spoolColour: selectedSet.has(spoolId) ? HIGHLIGHT_COLOUR : m.resolveSpoolBaseColour(spool),
      }));
      m.batchUpdateSpoolAppearance(spoolUpdates);

      const allStopes = Object.values(m.stopes).flat();
      const stopeNodeIds = allStopes
        .filter((s) => selectedSet.has(s?.data?.stopeId))
        .map((s) => s?.originNode?.id)
        .filter(Boolean) as string[];
      const highlightedNodeIds = new Set<string>([
        ...selectedIds.filter((id) => !!m.nodes[id]),
        ...stopeNodeIds,
      ]);
      const nodeUpdates = Object.entries(m.nodes).map(([nodeId, node]) => ({
        nodeId,
        nodeColour: highlightedNodeIds.has(nodeId) ? HIGHLIGHT_COLOUR : getDefaultNodeColour(node),
        nodeSize: highlightedNodeIds.has(nodeId) ? getDefaultNodeSize(node) * 1.5 : getDefaultNodeSize(node),
      }));
      m.batchUpdateNodeAppearance(nodeUpdates);
    }
  }, [selectedIds, entityType, model.loadGeneration]);

  React.useEffect(() => () => {
    clearAllHighlights(modelRef.current);
  }, []);

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkEditSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
      <Typography variant="h6">{i18n._('Bulk Edit')}</Typography>

      <FormControl size="small" fullWidth color="secondary">
        <InputLabel>{i18n._('Entity Type')}</InputLabel>
        <Select
          color="secondary"
          value={entityType}
          label={i18n._('Entity Type')}
          onChange={handleEntityTypeChange}
          disabled={isDeleting}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Node">Node</MenuItem>
          <MenuItem value="Stope">{i18n._('Stope')}</MenuItem>
          <MenuItem value="Spool">{i18n._('Spool')}</MenuItem>
        </Select>
      </FormControl>

      {selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={handleClearSelection}
            disabled={isDeleting}
          >
            Clear
          </Button>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary">
        {(() => {
          const isPlural = selectedIds.length !== 1;
          const labels: Record<typeof entityType, [string, string]> = {
            All: ['entity', 'entities'],
            Node: ['node', 'nodes'],
            Spool: ['spool', 'spools'],
            Stope: ['stope', 'stopes'],
          };
          const [singular, plural] = labels[entityType];
          return `${selectedIds.length} ${isPlural ? plural : singular} selected`;
        })()}
      </Typography>

      {selectedSpools.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {flowLoopMetres > 0
            ? i18n._('Total spool length: {total} ({pipe} pipe + {flowLoops} flow loops)', {
              total: formatLength(pipeMetres + flowLoopMetres, isImperial),
              pipe: formatLength(pipeMetres, isImperial),
              flowLoops: formatLength(flowLoopMetres, isImperial),
            })
            : i18n._('Total spool length: {total}', { total: formatLength(pipeMetres, isImperial) })}
        </Typography>
      )}

      {selectedIds.length > 0 && (
        <>
          <Divider />
          <BulkEditPropertiesPanel model={model} />
        </>
      )}
    </Box>
  );
};

export default BulkEditPanel;
